import React, { useContext, useState } from "react";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Table, Button, Select, Skeleton, message, Form } from "antd";
import { WalletOutlined, PlusOutlined } from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import PageHeader from "../components/PageHeader";
import AddExpenseModal from "../components/expense/AddExpenseModal";
import {
  createExpense,
  getExpensesByOwner,
} from "../lib/firebase/expenseModel";
import unitModel from "../lib/firebase/unitModel";
import { propertyKeys } from "../lib/queryKeys";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { LAYOUT_CONSTANTS, useBreakpoints } from "../lib/breakpoints";

const { Option } = Select;

const Expenses = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { isMobile } = useBreakpoints();
  const [isAddExpenseModalVisible, setIsAddExpenseModalVisible] =
    useState(false);
  const [expenseForm] = Form.useForm();
  const [selectedUnit, setSelectedUnit] = useState("all");

  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid),
    queryFn: () => unitModel.getByOwner(user.uid),
    enabled: !!user?.uid,
  });

  const { data: expensesResult, isLoading: isExpensesLoading } = useQuery({
    queryKey: propertyKeys.expenses(user?.uid),
    queryFn: () => getExpensesByOwner(),
    enabled: !!user?.uid,
  });

  const expenses = expensesResult?.expenses || [];
  const loading = isUnitsLoading || isExpensesLoading;

  const filteredExpenses =
    selectedUnit === "all"
      ? expenses
      : expenses.filter((e) => e.unitId === selectedUnit);

  const totalAmount = filteredExpenses.reduce(
    (sum, e) => sum + (Number(e.amount) || 0),
    0
  );

  const getUnitName = (unitId) => {
    const unit = units.find((u) => u.id === unitId);
    return unit?.name || getUnitDisplayAddress(unit) || unitId || "-";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    const d = date?.toDate?.() || new Date(date);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => formatDate(date),
      sorter: (a, b) => {
        const dA = a.date?.toDate?.() || new Date(a.date);
        const dB = b.date?.toDate?.() || new Date(b.date);
        return dA.getTime() - dB.getTime();
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amt) =>
        `$${(Number(amt) || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      sorter: (a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0),
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      ellipsis: true,
    },
    {
      title: "Payee / Vendor",
      dataIndex: "payee",
      key: "payee",
      ellipsis: true,
      render: (v) => v || "-",
    },
    {
      title: "Unit",
      dataIndex: "unitId",
      key: "unitId",
      render: (unitId) => getUnitName(unitId),
    },
  ];

  if (loading) {
    return (
      <Container>
        <PageHeader
          icon={WalletOutlined}
          title="Expenses"
          subtitle="Track property expenses for tax and reporting"
        />
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        icon={WalletOutlined}
        title="Expenses"
        subtitle="Track property expenses for tax and reporting"
      />

      <ExpensesCard
        title={
          <HeaderBar>
            <HeaderTopRow>
              {!isMobile && (
                <HeaderUnitFilter
                  placeholder="All Doors"
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                >
                  <Option value="all">All Doors</Option>
                  {units.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name || getUnitDisplayAddress(unit) || unit.id}
                    </Option>
                  ))}
                </HeaderUnitFilter>
              )}
              <HeaderTitle>Expenses ({filteredExpenses.length})</HeaderTitle>
              <AddExpenseButton
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsAddExpenseModalVisible(true)}
              >
                {!isMobile ? "Add Expense" : ""}
              </AddExpenseButton>
            </HeaderTopRow>
            {isMobile && (
              <HeaderFilterRow>
                <HeaderUnitFilter
                  placeholder="All Doors"
                  value={selectedUnit}
                  onChange={setSelectedUnit}
                >
                  <Option value="all">All Doors</Option>
                  {units.map((unit) => (
                    <Option key={unit.id} value={unit.id}>
                      {unit.name || getUnitDisplayAddress(unit) || unit.id}
                    </Option>
                  ))}
                </HeaderUnitFilter>
              </HeaderFilterRow>
            )}
          </HeaderBar>
        }
      >
        {filteredExpenses.length === 0 ? (
          <ExpensesEmptyState>
            <ExpensesEmptyIcon>
              <WalletOutlined />
            </ExpensesEmptyIcon>
            <ExpensesEmptyTitle>No Expenses Yet</ExpensesEmptyTitle>
            <ExpensesEmptySubtitle>
              Track property expenses like repairs, utilities, and maintenance
              for tax reporting.
            </ExpensesEmptySubtitle>
            <EmptyStateAddButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsAddExpenseModalVisible(true)}
            >
              Add Expense
            </EmptyStateAddButton>
          </ExpensesEmptyState>
        ) : (
          <>
            <SummaryRow>
              Total: $
              {totalAmount.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </SummaryRow>
            <Table
              columns={columns}
              dataSource={filteredExpenses}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} expenses`,
              }}
            />
          </>
        )}
      </ExpensesCard>

      <AddExpenseModal
        open={isAddExpenseModalVisible}
        onCancel={() => {
          setIsAddExpenseModalVisible(false);
          expenseForm.resetFields();
        }}
        onSubmit={async (values) => {
          const result = await createExpense({
            date: values.date?.toDate?.() || values.date,
            payee: values.payee,
            amount: values.amount,
            unitId: values.unitId,
            category: values.category,
            description: values.description,
            receiptUrl: values.receiptUrl || "",
          });
          if (result.success) {
            message.success("Expense logged");
            setIsAddExpenseModalVisible(false);
            expenseForm.resetFields();
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.expenses(user?.uid),
            });
          } else {
            message.error(result.error || "Failed to log expense");
          }
        }}
        units={units}
        form={expenseForm}
      />
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
  min-height: 44px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
    min-height: auto;
  }
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const HeaderFilterRow = styled.div`
  width: 100%;
  margin-top: 0;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const HeaderUnitFilter = styled(Select)`
  flex-shrink: 0;
  width: 180px;
  display: inline-flex !important;
  align-items: center !important;
  align-self: center !important;

  .ant-select {
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selector {
    position: relative !important;
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 8px !important;
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    color: var(--color-text) !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    color: var(--color-text) !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-arrow {
    position: absolute !important;
    color: var(--color-text) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    top: 50% !important;
    right: 11px !important;
    margin: 0 !important;
    transform: translateY(-50%) !important;
    line-height: 0 !important;
    pointer-events: none !important;
  }

  .ant-select-selection-search {
    display: flex;
    align-items: center;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
  }
`;

const HeaderTitle = styled.span`
  flex: 1;
  font-family: var(--font-primary);
  font-weight: 600;
  font-size: 1.25rem;
  color: white;
  text-align: center;
  min-width: 0;

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const ExpensesCard = styled(Card)`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
  border: 2px solid var(--color-border-light);

  .ant-card-head {
    background: linear-gradient(
      135deg,
      var(--color-primary-darker) 0%,
      #4a7c59 100%
    ) !important;
    border-bottom: none;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  .ant-card-head-title {
    color: white !important;
    padding: 1rem 2rem;
    flex: 1;
    min-width: 0;
  }

  .ant-card-body {
    padding: 1.5rem 2rem;
  }

  .ant-table-thead > tr > th {
    background: var(--color-bg-tertiary);
    border-bottom: 2px solid var(--color-border-light);
    font-family: var(--font-secondary);
    font-weight: 600;
    color: var(--color-text);
  }

  .ant-table-tbody > tr > td {
    color: var(--color-text);
  }

  .ant-table-tbody > tr:hover > td {
    background: var(--color-primary-glow);
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 1rem;
    }
  }
`;

const AddExpenseButton = styled(Button)`
  flex-shrink: 0;
  display: flex !important;
  align-items: center !important;
  background: transparent !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  height: 32px;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1) !important;
  }

  &:hover,
  &:focus,
  &:active {
    background: transparent !important;
    border: none !important;
    color: white !important;
  }
`;

const EmptyStateAddButton = styled(Button)`
  background: linear-gradient(
    135deg,
    var(--color-primary-darker) 0%,
    #4a7c59 100%
  ) !important;
  border: none !important;
  color: white !important;
  font-weight: 600 !important;
  padding: 0 2rem !important;
  height: 44px !important;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1) !important;
  }

  &:hover {
    background: linear-gradient(
      135deg,
      #1e4d1e 0%,
      var(--color-primary-darker) 100%
    ) !important;
    color: white !important;
  }
`;

const ExpensesEmptyState = styled.div`
  padding: 4rem 2rem;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid var(--color-border-light);
  margin: 1rem 0;
`;

const ExpensesEmptyIcon = styled.div`
  margin-bottom: 1.5rem;

  .anticon {
    font-size: 3.5rem;
    color: var(--color-primary-darker);
    opacity: 0.9;
  }
`;

const ExpensesEmptyTitle = styled.h3`
  font-family: var(--font-primary);
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.75rem 0;
`;

const ExpensesEmptySubtitle = styled.p`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-text-light);
  margin: 0 0 1.5rem 0;
  line-height: 1.6;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const SummaryRow = styled.div`
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
`;

export default Expenses;
